import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import BuilderOrganizationControls from '../BuilderOrganizationControls';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  TextField,
  Autocomplete,
  useMediaQuery,
} from '@mui/material';
import Tooltip from '../LibraryTooltip';
import { useTheme } from '@mui/material/styles';
import {
  PublishOutlined as PublishIcon,
  CloseOutlined as CloseIcon,
  DeleteOutlined as DeleteIcon,
  PersonOutlined as PersonLevelIcon,
  AdminPanelSettingsOutlined as AdminLevelIcon,
} from '@mui/icons-material';
import PermissionSettings from '../permissions/PermissionSettings';
import MarkdownPreviewEditor from '../MarkdownPreviewEditor';
import {
  borderedDescriptionShellSx,
  builderOutlinedControlSx,
  embeddedMarkdownTextFieldSx,
  instructionBodyTextSx,
  instructionPreviewMarkdownSx,
} from '../markdownBodySx';
import { listCompanyUsers, listTeams } from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import { useSessionData } from '../../../contexts/SessionDataContext';
import { resolveAccountCategoryOptions } from '../../../constants/accountCategories';
import { secondarySectionHeaderSx } from '../../../designTokens';
import useAccountSettings from '../hooks/useAccountSettings';

const VISIBILITY_LEVELS = ['private', 'team', 'organization', 'custom'];

const VISIBILITY_TOOLTIPS = {
  private: 'Only you can see and manage this project.',
  team: 'Share with your team only.',
  organization: 'Visible to everyone in your organization.',
  custom: 'Set granular access: choose specific people, teams, or groups.',
};

const PROJECT_FIELD_LABEL_SX = {
  ...secondarySectionHeaderSx,
  color: 'text.primary',
  display: 'block',
  mb: 0.75,
  px: 1.25,
};

const PROJECT_DESCRIPTION_HELPER =
  'Describe what this workspace is for. Add capabilities and knowledge as you go.';

const EMPTY_PERMISSIONS = {
  companyAccess: false,
  teamAccess: [],
  groupAccess: [],
  individualAccess: [],
  canEdit: [],
  canShare: [],
  canDelete: [],
  viewOnlyAccess: [],
};

const CollectionBuilderDialog = ({
  open,
  onClose,
  isEditing = false,
  initialCollectionData = null,
  onSave,
  onDelete = null,
  userProfile = null,
}) => {
  const { user } = useAuth();
  const { getGroups } = useSessionData();
  const { settings: accountSettings } = useAccountSettings();
  const categoryOptions = useMemo(
    () => resolveAccountCategoryOptions(accountSettings?.categories),
    [accountSettings?.categories]
  );
  const [editingDescription, setEditingDescription] = useState(false);
  const [accessList, setAccessList] = useState([]);
  const openAddAccessRef = useRef(null);
  const [collectionData, setCollectionData] = useState({
    title: '',
    description: '',
    category: '',
    tags: [],
    isPublic: false,
    allowCopying: true,
    allowEditing: false,
    permissions: { ...EMPTY_PERMISSIONS },
  });
  const [saving, setSaving] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const permissionLevels = {
    interact: {
      label: 'Interact',
      icon: <PersonLevelIcon />,
      description:
        'Can view capabilities and knowledge in the project and add capabilities and knowledge to the project',
      permissions: ['view', 'add'],
    },
    owner: {
      label: 'Co-owner',
      icon: <AdminLevelIcon />,
      description:
        'Can do everything including edit, delete, and manage permissions of the project',
      permissions: ['view', 'add', 'edit', 'delete', 'manage-permissions'],
    },
  };

  const visibilityIndex = useMemo(() => {
    const p = collectionData.permissions || {};
    const noAccess =
      !p.companyAccess &&
      (!p.teamAccess || p.teamAccess.length === 0) &&
      (!p.groupAccess || p.groupAccess.length === 0) &&
      (!p.individualAccess || p.individualAccess.length === 0);
    if (noAccess) return 0;
    if (p.companyAccess) return 2;
    const userTeamId = userProfile?.teamId;
    const singleTeam = p.teamAccess && p.teamAccess.length === 1 && p.teamAccess[0] === userTeamId;
    const viewOnly = Array.isArray(p.viewOnlyAccess) ? p.viewOnlyAccess : [];
    const canEdit = Array.isArray(p.canEdit) ? p.canEdit : [];
    const canDelete = Array.isArray(p.canDelete) ? p.canDelete : [];
    const teamHasCustomRoles =
      userTeamId &&
      (canEdit.includes(userTeamId) || canDelete.includes(userTeamId) || viewOnly.includes(userTeamId));
    if (singleTeam && !teamHasCustomRoles) return 1;
    return 3;
  }, [collectionData.permissions, userProfile?.teamId]);

  const currentVisibility = VISIBILITY_LEVELS[visibilityIndex];

  const handleVisibilityChange = (_, value) => {
    if (value == null) return;
    const level =
      typeof value === 'string' && VISIBILITY_LEVELS.includes(value)
        ? value
        : VISIBILITY_LEVELS[typeof value === 'number' ? value : Number(value)];
    const userTeamId = userProfile?.teamId || null;
    const creatorId = user?.id;
    const emptyArrays = {
      groupAccess: [],
      individualAccess: [],
      canEdit: [],
      canShare: [],
      canDelete: [],
      viewOnlyAccess: [],
    };
    if (level === 'private') {
      setCollectionData((prev) => ({
        ...prev,
        permissions: { companyAccess: false, teamAccess: [], ...emptyArrays },
      }));
    } else if (level === 'team') {
      setCollectionData((prev) => ({
        ...prev,
        permissions: {
          companyAccess: false,
          teamAccess: userTeamId ? [userTeamId] : [],
          ...emptyArrays,
        },
      }));
    } else if (level === 'organization') {
      setCollectionData((prev) => ({
        ...prev,
        permissions: {
          companyAccess: true,
          teamAccess: userTeamId ? [userTeamId] : [],
          ...emptyArrays,
        },
      }));
    } else {
      const existingIndividual = Array.isArray(collectionData.permissions?.individualAccess)
        ? collectionData.permissions.individualAccess
        : [];
      const hasCreator = creatorId && existingIndividual.includes(creatorId);
      const individualAccess =
        hasCreator || !creatorId ? existingIndividual : [creatorId, ...existingIndividual];
      const existingCustom = {
        canEdit: Array.isArray(collectionData.permissions?.canEdit)
          ? collectionData.permissions.canEdit
          : [],
        canShare: Array.isArray(collectionData.permissions?.canShare)
          ? collectionData.permissions.canShare
          : [],
        canDelete: Array.isArray(collectionData.permissions?.canDelete)
          ? collectionData.permissions.canDelete
          : [],
        viewOnlyAccess: Array.isArray(collectionData.permissions?.viewOnlyAccess)
          ? collectionData.permissions.viewOnlyAccess
          : [],
      };
      setCollectionData((prev) => ({
        ...prev,
        permissions: {
          companyAccess: false,
          teamAccess: [],
          groupAccess: [],
          individualAccess,
          canEdit: existingCustom.canEdit,
          canShare: existingCustom.canShare,
          canDelete: existingCustom.canDelete,
          viewOnlyAccess: existingCustom.viewOnlyAccess,
        },
      }));
    }
  };

  const openAddCustomAccess = () => {
    openAddAccessRef.current?.();
  };

  const rebuildAccessListFromPermissions = useCallback(
    async (perms) => {
      if (!user?.companyId) return;
      try {
        const newAccessList = [];
        const getRoleForEntity = (entityId) => {
          const canDelete = perms.canDelete?.includes(entityId) || false;
          if (canDelete) return 'owner';
          const hasAccess =
            perms.individualAccess?.includes(entityId) ||
            perms.teamAccess?.includes(entityId) ||
            perms.groupAccess?.includes(entityId);
          return hasAccess ? 'interact' : null;
        };
        if (perms.individualAccess && perms.individualAccess.length > 0) {
          try {
            const usersResponse = await listCompanyUsers(user.companyId);
            const allUsers = usersResponse.users || [];
            const creatorId = collectionData?.userId || user?.id;
            perms.individualAccess.forEach((userId) => {
              if (creatorId && userId === creatorId) return;
              const userObj = allUsers.find((u) => u.id === userId);
              if (userObj) {
                newAccessList.push({
                  id: userObj.id,
                  type: 'user',
                  name:
                    userObj.name ||
                    `${userObj.firstName || ''} ${userObj.lastName || ''}`.trim() ||
                    userObj.email,
                  email: userObj.email,
                  role: getRoleForEntity(userId),
                  description: userObj.role || 'USER',
                });
              }
            });
          } catch (error) {
            console.error('Failed to load users for access list:', error);
          }
        }
        if (perms.teamAccess && perms.teamAccess.length > 0) {
          try {
            const teamsResponse = await listTeams(user.companyId);
            const allTeams = teamsResponse.teams || [];
            perms.teamAccess.forEach((teamId) => {
              const team = allTeams.find((t) => t.id === teamId);
              if (team) {
                newAccessList.push({
                  id: team.id,
                  type: 'team',
                  name: team.name,
                  role: getRoleForEntity(teamId),
                  description: team.description || '',
                  memberCount: team.memberCount || 0,
                });
              }
            });
          } catch (error) {
            console.error('Failed to load teams for access list:', error);
          }
        }
        if (perms.groupAccess && perms.groupAccess.length > 0) {
          try {
            const groupsList = await getGroups({ force: false });
            const allGroups = Array.isArray(groupsList) ? groupsList : [];
            perms.groupAccess.forEach((groupId) => {
              const group = allGroups.find((g) => g.id === groupId);
              if (group) {
                newAccessList.push({
                  id: group.id,
                  type: 'group',
                  name: group.name,
                  role: getRoleForEntity(groupId),
                  description: group.description || '',
                  memberCount: group.memberCount || 0,
                });
              }
            });
          } catch (error) {
            console.error('Failed to load groups for access list:', error);
          }
        }
        setAccessList(newAccessList);
      } catch (error) {
        console.error('Error rebuilding access list from permissions:', error);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- user?.id, collectionData?.userId intentionally omitted
    [user?.companyId, getGroups]
  );

  useEffect(() => {
    if (
      collectionData?.permissions &&
      currentVisibility === 'custom' &&
      collectionData?.id &&
      user?.companyId
    ) {
      const perms = collectionData.permissions;
      const hasIndividual = perms.individualAccess && perms.individualAccess.length > 0;
      const hasTeam = perms.teamAccess && perms.teamAccess.length > 0;
      const hasGroup = perms.groupAccess && perms.groupAccess.length > 0;
      if (hasIndividual || hasTeam || hasGroup) {
        rebuildAccessListFromPermissions(perms);
      }
    }
  }, [collectionData?.permissions, collectionData?.id, user?.companyId, currentVisibility, rebuildAccessListFromPermissions]);

  const resetFormState = useCallback(() => {
    setCollectionData({
      title: '',
      description: '',
      category: '',
      tags: [],
      isPublic: false,
      allowCopying: true,
      allowEditing: false,
      permissions: {
        companyAccess: true,
        teamAccess: userProfile?.teamId ? [userProfile.teamId] : [],
        groupAccess: [],
        individualAccess: [],
        canEdit: [],
        canShare: [],
        canDelete: [],
        viewOnlyAccess: [],
      },
    });
  }, [userProfile?.teamId]);

  useEffect(() => {
    if (open && isEditing && initialCollectionData) {
      const normalized = {
        ...initialCollectionData,
        permissions: {
          ...initialCollectionData.permissions,
          viewOnlyAccess: initialCollectionData.permissions?.viewOnlyAccess ?? [],
        },
      };
      setCollectionData(normalized);
    } else if (open && !isEditing) {
      if (initialCollectionData) {
        resetFormState();
        setCollectionData((prev) => ({
          ...prev,
          ...initialCollectionData,
          permissions: {
            ...prev.permissions,
            ...(initialCollectionData.permissions || {}),
            viewOnlyAccess: initialCollectionData.permissions?.viewOnlyAccess ?? prev.permissions.viewOnlyAccess ?? [],
          },
        }));
      } else {
        resetFormState();
      }
    }
  }, [open, isEditing, initialCollectionData, resetFormState]);

  const handleClose = () => {
    resetFormState();
    setSaving(false);
    setEditingDescription(false);
    onClose();
  };

  const handleInputChange = (field, value) => {
    setCollectionData((prev) => ({ ...prev, [field]: value }));
  };

  const handlePermissionsChange = (newPermissions) => {
    setCollectionData((prev) => ({ ...prev, permissions: newPermissions }));
  };


  const buildSavePayload = () => {
    const payload = { ...collectionData };
    delete payload.whereToUse;
    delete payload.usagePlatform;
    delete payload.usagePlatformOther;
    return {
      ...payload,
      category: (collectionData.category || '').trim() || 'Operations',
      tags: collectionData.tags || [],
    };
  };

  const handleSave = async () => {
    if (!onSave) return;
    setSaving(true);
    try {
      await onSave(buildSavePayload(), isEditing);
      handleClose();
    } catch (err) {
      console.error('Error saving collection:', err);
    } finally {
      setSaving(false);
    }
  };


  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          display: 'flex',
          flexDirection: 'column',
          borderRadius: { xs: 0, sm: '8px' },
          width: { xs: '100%', sm: 'min(800px, 92vw)' },
          maxHeight: { xs: '100vh', sm: '88vh' },
          height: { xs: '100vh', sm: 'auto' },
        },
      }}
    >
      <DialogContent
        sx={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column', p: 3, minHeight: 0 }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, maxWidth: 720 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="h6" component="h1" sx={{ fontSize: { xs: '1.125rem', sm: '1.25rem' }, fontWeight: 600 }}>
              {isEditing ? 'Edit Project' : 'Project Builder'}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0, flexWrap: 'wrap', ml: 'auto' }}>
              <BuilderOrganizationControls
                visibility={currentVisibility} visibilityOptions={VISIBILITY_LEVELS}
                visibilityDescriptions={VISIBILITY_TOOLTIPS}
                onVisibilityChange={(value) => handleVisibilityChange(null, value)}
                onManagePermissions={openAddCustomAccess}
              />
              {isEditing && onDelete ? (
                <Tooltip title="Delete project">
                  <IconButton
                    onClick={() => {
                      handleClose();
                      onDelete();
                    }}
                    sx={{ color: 'error.main', '&:hover': { backgroundColor: 'error.50' } }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              ) : null}
              <Tooltip title="Close">
                <IconButton onClick={handleClose} aria-label="Close create project dialog">
                  <CloseIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {/* Project details */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Box>
              <Typography component="span" sx={PROJECT_FIELD_LABEL_SX}>
                Project Name
              </Typography>
              <Box sx={borderedDescriptionShellSx}>
                <TextField
                  fullWidth
                  variant="standard"
                  value={collectionData.title || ''}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  placeholder="Give your project a name"
                  InputProps={{ disableUnderline: true }}
                  sx={{
                    ...embeddedMarkdownTextFieldSx,
                    '& .MuiInputBase-root': {
                      ...embeddedMarkdownTextFieldSx['& .MuiInputBase-root'],
                      color: 'text.primary',
                    },
                    '& .MuiInputBase-input': {
                      ...embeddedMarkdownTextFieldSx['& .MuiInputBase-input'],
                      ...instructionBodyTextSx,
                      color: 'text.primary',
                      '&::placeholder': {
                        color: 'text.secondary',
                        fontStyle: 'italic',
                        opacity: 1,
                      },
                    },
                  }}
                  inputProps={{ 'aria-label': 'Project name' }}
                />
              </Box>
            </Box>

            <Box>
              <Typography component="span" sx={PROJECT_FIELD_LABEL_SX}>
                Description
              </Typography>
              <Box sx={borderedDescriptionShellSx}>
                <MarkdownPreviewEditor
                  value={collectionData.description || ''}
                  onChange={(text) => handleInputChange('description', text)}
                  mode={editingDescription ? 'edit' : 'preview'}
                  onModeChange={(mode) => setEditingDescription(mode === 'edit')}
                  showToggle={false}
                  compact
                  embedded
                  minRows={2}
                  contentTextSx={instructionBodyTextSx}
                  placeholder={PROJECT_DESCRIPTION_HELPER}
                  renderEditor={() => (
                    <TextField
                      autoFocus
                      fullWidth
                      multiline
                      minRows={2}
                      variant="standard"
                      value={collectionData.description || ''}
                      onChange={(e) => handleInputChange('description', e.target.value)}
                      onBlur={() => setEditingDescription(false)}
                      placeholder={PROJECT_DESCRIPTION_HELPER}
                      InputProps={{ disableUnderline: true }}
                      sx={embeddedMarkdownTextFieldSx}
                    />
                  )}
                  previewSx={{ px: 0, py: 0, '& .markdown-body': instructionPreviewMarkdownSx }}
                />
              </Box>
            </Box>

            <Box>
              <Typography component="span" sx={PROJECT_FIELD_LABEL_SX}>
                Category
              </Typography>
              <Autocomplete
                freeSolo
                forcePopupIcon
                options={categoryOptions.filter((category) => category !== 'Other')}
                value={collectionData.category || null}
                onChange={(_, value) => handleInputChange('category', value || '')}
                onInputChange={(_, value, reason) => { if (reason === 'input') handleInputChange('category', value); }}
                renderInput={(params) => <TextField {...params} size="small" placeholder="Select or enter category" inputProps={{ ...params.inputProps, 'aria-label': 'Category' }} sx={builderOutlinedControlSx} />}
              />
            </Box>
          </Box>

        </Box>

        <Box sx={{ position: 'absolute', left: -9999, width: 1, height: 1, overflow: 'hidden' }} aria-hidden="true">
          <PermissionSettings
            permissions={collectionData.permissions}
            onPermissionsChange={handlePermissionsChange}
            userProfile={userProfile}
            isEditing={isEditing}
            entityType="collection"
            hideLegacyToggles
            visibility={currentVisibility}
            openAddAccessRef={openAddAccessRef}
            permissionLevels={permissionLevels}
            accessList={accessList}
            onAccessListChange={setAccessList}
            creatorId={collectionData.userId || user?.id}
            entityData={isEditing ? collectionData : null}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ flexShrink: 0, p: 3, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
        <Button onClick={handleClose} disabled={saving}>
          Cancel
        </Button>
        <Box sx={{ flex: 1 }} />
        <Tooltip
          title={!collectionData.title.trim() ? 'Project name required' : ''}
          placement="top"
          disableHoverListener={!!collectionData.title.trim()}
        >
          <span>
            <Button
              variant="contained"
              onClick={handleSave}
              startIcon={<PublishIcon />}
              disabled={saving || !collectionData.title.trim()}
              sx={{ minWidth: 120 }}
            >
              {saving ? 'Saving...' : isEditing ? 'Update Project' : 'Create Project'}
            </Button>
          </span>
        </Tooltip>
      </DialogActions>
    </Dialog>
  );
};

export default CollectionBuilderDialog;
